import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Newspaper,
  Calendar,
  Stethoscope,
  Building2,
  CalendarCheck,
  MessageSquare,
  Image,
  Bell,
  Clock,
  TrendingUp,
  Plus,
  Eye,
  AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/api/admin/dashboard/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  const statCards = [
    { label: 'Total News', value: stats?.total_news || 0, icon: Newspaper, color: 'bg-blue-500', link: '/admin/news' },
    { label: 'Total Events', value: stats?.total_events || 0, icon: Calendar, color: 'bg-purple-500', link: '/admin/events' },
    { label: 'Total Doctors', value: stats?.total_doctors || 0, icon: Stethoscope, color: 'bg-green-500', link: '/admin/doctors' },
    { label: 'Departments', value: stats?.total_departments || 0, icon: Building2, color: 'bg-orange-500', link: '/admin/departments' },
    { label: 'Total Bookings', value: stats?.total_bookings || 0, icon: CalendarCheck, color: 'bg-teal-500', link: '/admin/bookings' },
    { label: 'Pending Bookings', value: stats?.pending_bookings || 0, icon: Clock, color: 'bg-yellow-500', link: '/admin/bookings?status=pending' },
    { label: 'Total Messages', value: stats?.total_messages || 0, icon: MessageSquare, color: 'bg-pink-500', link: '/admin/messages' },
    { label: 'Unread Messages', value: stats?.unread_messages || 0, icon: AlertCircle, color: 'bg-red-500', link: '/admin/messages' },
    { label: 'Gallery Items', value: stats?.total_gallery || 0, icon: Image, color: 'bg-indigo-500', link: '/admin/gallery' },
    { label: 'Announcements', value: stats?.total_announcements || 0, icon: Bell, color: 'bg-cyan-500', link: '/admin/announcements' },
  ];

  const quickActions = [
    { label: 'Add News', icon: Newspaper, link: '/admin/news', action: 'new' },
    { label: 'Add Event', icon: Calendar, link: '/admin/events', action: 'new' },
    { label: 'Add Doctor', icon: Stethoscope, link: '/admin/doctors', action: 'new' },
    { label: 'View Bookings', icon: CalendarCheck, link: '/admin/bookings' },
    { label: 'Upload Images', icon: Image, link: '/admin/gallery' },
  ];

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create': return 'text-green-600 bg-green-50';
      case 'update': return 'text-blue-600 bg-blue-50';
      case 'delete': return 'text-red-600 bg-red-50';
      case 'login': return 'text-purple-600 bg-purple-50';
      case 'logout': return 'text-slate-600 bg-slate-50';
      case 'approve': return 'text-teal-600 bg-teal-50';
      case 'reject': return 'text-orange-600 bg-orange-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-800">Dashboard</h1>
            <p className="text-slate-600">Welcome to Baobab Hospital Admin Panel</p>
          </div>
          <div className="flex gap-2">
            <Link to="/" target="_blank">
              <Button variant="outline" size="sm">
                <Eye className="w-4 h-4 mr-2" />
                View Website
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {statCards.map((stat, index) => (
            <Link key={index} to={stat.link}>
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">{stat.label}</p>
                      <p className="text-2xl font-bold text-slate-800">
                        {loading ? '-' : stat.value}
                      </p>
                    </div>
                    <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center`}>
                      <stat.icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {quickActions.map((action, index) => (
                <Link key={index} to={action.link}>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                  >
                    <action.icon className="w-4 h-4 mr-2" />
                    {action.label}
                    {action.action === 'new' && <Plus className="w-4 h-4 ml-auto" />}
                  </Button>
                </Link>
              ))}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {stats?.recent_activities?.length > 0 ? (
                  stats.recent_activities.map((activity, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getActionColor(activity.action_type)}`}>
                        {activity.action_type}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-700 truncate">{activity.description}</p>
                        <p className="text-xs text-slate-500">
                          {activity.admin_name} • {activity.module_name} • {formatTime(activity.created_at)}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-slate-500 py-8">No recent activity</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
